import React from "react";
import Seo from "../components/Seo.jsx";

/* Dashboard screen — overview, stats, top opportunities preview.
   More breathable, with animated count-up stats and a richer
   "By subreddit" panel (segmented confidence bars + new badges). */

function CountUp({ value, duration = 900 }) {
  const target = Number(value);
  const isNum = !Number.isNaN(target);
  // Start at target if we can't animate (hidden tab / reduced motion);
  // otherwise start at 0 and tween up.
  const canAnimate =
    isNum &&
    typeof document !== "undefined" &&
    !document.hidden &&
    !(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
  const [n, setN] = React.useState(canAnimate ? 0 : (isNum ? target : value));

  React.useEffect(() => {
    if (!isNum) { setN(value); return; }
    if (!canAnimate) { setN(target); return; }
    let raf;
    let start = null;
    const animate = (ts) => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    // Safety net: force final value even if rAF gets paused.
    const failsafe = setTimeout(() => setN(target), duration + 200);
    return () => { cancelAnimationFrame(raf); clearTimeout(failsafe); };
  }, [target, duration, isNum, value, canAnimate]);

  return <>{n}</>;
}

/* Latest-scan summary pill — sits under the page subtitle and tells the
   user what the most recent scan actually did, even (especially) when
   it surfaced 0 new opportunities. Keeps the dashboard from feeling
   broken after a quiet scan because the inbox below is still populated
   from earlier scans. */
function ScanSummaryBanner({ candidates, surfaced, activeCount }) {
  const checkedLabel =
    candidates > 0
      ? `${candidates.toLocaleString()} candidate${candidates === 1 ? "" : "s"} checked`
      : "0 candidates checked";

  // The positive "X new opportunities" summary now lives in the topbar as a
  // miniature pill — the dashboard only carries the neutral, quiet-scan copy.
  if (surfaced > 0) return null;
  if (activeCount > 0) {
    return (
      <div className="scan-summary scan-summary-neutral" role="status">
        <window.I.ShieldCheck size={14} />
        <span>
          All caught up. We scanned {checkedLabel} and didn’t surface anything
          new. Your <strong>{activeCount} existing opportunit{activeCount === 1 ? "y" : "ies"}</strong>{" "}
          {activeCount === 1 ? "is" : "are"} still active.
        </span>
      </div>
    );
  }
  return (
    <div className="scan-summary scan-summary-neutral" role="status">
      <window.I.ShieldCheck size={14} />
      <span>
        Latest scan checked {checkedLabel} and didn’t find anything strong
        enough to surface. Try widening the time window or adjusting your
        profile.
      </span>
    </div>
  );
}

function StatTile({ label, value, meta, icon, accent }) {
  const IconCmp = icon ? window.I[icon] : null;
  return (
    <div className={"stat stat-v2 " + (accent ? "stat-accent" : "")}>
      <div className="stat-label">
        {IconCmp && <IconCmp size={13} />}
        {label}
      </div>
      <div className="stat-value">
        <CountUp value={value} />
      </div>
      {meta && <div className="stat-meta">{meta}</div>}
    </div>
  );
}

function radarSeededUnit(seed) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967295;
}

const SRT_SWEEP_MS = 4200;
const SRT_HIT_DEG = 7;
const SRT_TRAIL_DEG = 68;

function radarDotAngle(x, y) {
  return ((Math.atan2(x - 50, -(y - 50)) * 180) / Math.PI + 360) % 360;
}

function radarBlipIntensity(sweepDeg, x, y) {
  const behind = ((sweepDeg - radarDotAngle(x, y) + 360) % 360);
  if (behind > SRT_TRAIL_DEG) return 0;
  if (behind <= SRT_HIT_DEG) {
    const hit = 1 - behind / SRT_HIT_DEG;
    return 0.62 + 0.38 * hit * hit;
  }
  const fade = 1 - (behind - SRT_HIT_DEG) / (SRT_TRAIL_DEG - SRT_HIT_DEG);
  return fade * 0.72;
}

function pickRadarPosition(sub, index, others, salt = "") {
  const minGap = others.length > 5 ? 20 : 23;

  for (let attempt = 0; attempt < 48; attempt += 1) {
    const seed = `${sub}:${index}:${salt}:${attempt}`;
    const angle = radarSeededUnit(seed + ":a") * Math.PI * 2;
    const radius = 16 + radarSeededUnit(seed + ":r") * 22;
    const candidate = {
      sub,
      x: 50 + Math.cos(angle) * radius,
      y: 50 + Math.sin(angle) * radius,
    };
    const nearestGap = others.length
      ? Math.min(...others.map((p) => Math.hypot(candidate.x - p.x, candidate.y - p.y)))
      : Infinity;
    if (nearestGap >= minGap) return candidate;
  }
  return null;
}

function buildRadarPositions(plotted) {
  const placed = [];

  return plotted.map((sub, i) => {
    const candidate = pickRadarPosition(sub, i, placed, "init");
    if (candidate) placed.push(candidate);
    return candidate;
  }).filter(Boolean);
}

/* Live "on the radar" tile — replaces the old static "Reviewed" stat with
   a small spinning radar that plots each monitored subreddit as a blip.
   Compact enough to sit in the stat row; the sweep + blips read as "we're
   actively watching these communities". */
function RadarTile({ profile }) {
  const subs = (profile.subreddits || []).map((s) =>
    typeof s === "string"
      ? (s.startsWith("r/") ? s : "r/" + s)
      : "r/" + String(s?.name || s?.subreddit || "").replace(/^r\//, "")
  );
  const plottedKey = subs.slice(0, 8).join("\0");
  const plotted = React.useMemo(
    () => subs.slice(0, 8),
    [plottedKey]
  );
  const subCount = subs.length;

  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

  const [blips, setBlips] = React.useState(() => buildRadarPositions(plotted));
  const [sweepDeg, setSweepDeg] = React.useState(0);
  const sweepRef = React.useRef(0);
  const relocateRef = React.useRef({ idx: 0, last: 0 });

  React.useEffect(() => {
    setBlips(buildRadarPositions(plotted));
  }, [plottedKey]);

  React.useEffect(() => {
    if (reduceMotion || !plotted.length) return undefined;

    let raf = 0;
    let start = performance.now();

    const tick = (ts) => {
      const deg = (((ts - start) % SRT_SWEEP_MS) / SRT_SWEEP_MS) * 360;
      sweepRef.current = deg;
      setSweepDeg(deg);

      if (ts - relocateRef.current.last > 2400 + (relocateRef.current.idx % 3) * 400) {
        relocateRef.current.last = ts;
        setBlips((prev) => {
          if (prev.length < 2) return prev;

          const tryCount = prev.length;
          for (let n = 0; n < tryCount; n += 1) {
            const idx = (relocateRef.current.idx + n) % prev.length;
            const target = prev[idx];
            if (radarBlipIntensity(sweepRef.current, target.x, target.y) > 0.03) {
              continue;
            }

            const others = prev.filter((_, i) => i !== idx);
            const nextPos = pickRadarPosition(
              target.sub,
              idx,
              others,
              `${Date.now()}:${n}`
            );
            if (!nextPos) continue;

            relocateRef.current.idx = idx + 1;
            const next = [...prev];
            next[idx] = nextPos;
            return next;
          }

          relocateRef.current.last = ts - 1600;
          return prev;
        });
      }

      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [plotted.length, reduceMotion, plottedKey]);

  return (
    <div className="stat stat-v2 stat-radar-tile">
      <div className="stat-label">
        <window.I.Eye size={13} />
        On the radar
      </div>
      <div className="stat-radar-body">
        <div className="stat-radar-main">
          <div className="stat-value">
            <CountUp value={subCount} />
          </div>
          <div className="stat-meta">
            <span>subreddit{subCount !== 1 ? "s" : ""} monitored</span>
          </div>
        </div>
        <div className="stat-radar-viz" aria-hidden="true">
          <span className="srt-ring srt-ring-1" />
          <span className="srt-ring srt-ring-2" />
          <span
            className="srt-sweep"
            style={reduceMotion ? undefined : { transform: `rotate(${sweepDeg}deg)` }}
          />
          <span
            className="srt-core"
            style={{ background: profile?.color || "var(--accent)" }}
          />
          {blips.map((p, i) => {
            const color = subColor(p.sub);
            const intensity = reduceMotion
              ? 0.38
              : radarBlipIntensity(sweepDeg, p.x, p.y);
            const scale = 0.74 + intensity * 0.46;
            const glow = intensity > 0.04
              ? `0 0 ${6 + intensity * 18}px color-mix(in oklch, ${color} ${Math.round(48 + intensity * 52)}%, transparent), 0 0 ${2 + intensity * 5}px color-mix(in oklch, ${color} ${Math.round(70 + intensity * 30)}%, transparent)`
              : "none";
            return (
              <span
                key={`${p.sub}-${i}`}
                className="srt-sat"
                style={{
                  left: `${p.x}%`,
                  top: `${p.y}%`,
                  background: color,
                  opacity: intensity,
                  transform: `translate(-50%, -50%) scale(${scale})`,
                  filter: intensity > 0.35 ? `brightness(${1 + intensity * 0.28})` : undefined,
                  boxShadow: glow,
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DashboardScreen({ state, onNavigate, onOpenOpp, onFeedback, onScanNow }) {
  const {
    opps, oppsLoading, profile, scoreMode, isScanning,
    profilesLoading, latestScan, firstScan, scanSubreddits,
  } = state;
  const { user } = window.useCurrentUser();
  const newCount = opps.filter((o) => o.isNew).length;
  const highCount = opps.filter((o) => o.tier === "high").length;
  const monitored = profile.subreddits.length;


  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" :
      hour < 18 ? "Good afternoon" :
        "Good evening";
  // First name only — full names get too long next to the greeting.
  const displayName = user?.name?.trim().split(/\s+/)[0] || "there";

  // Real "last scan" label — sourced from the scan row the API ships with,
  // not the profile.updated_at (which changes when the user edits the form).
  const lastScanLabel = React.useMemo(() => {
    const iso = latestScan?.finished_at || latestScan?.started_at;
    return iso ? window.relativeAgo(iso) : null;
  }, [latestScan]);

  // Persistent inbox semantics: the dashboard NEVER blanks just because
  // the latest scan turned up nothing — it still shows the active
  // opportunities the user has accumulated. The "scanSummary" pill below
  // explains what the latest scan actually did, separately from the
  // inbox state, so the user never wonders "where did my opportunities
  // go?" after a quiet scan.
  const scanCandidates = Number(latestScan?.candidates_count || 0);
  const scanSurfaced = Number(latestScan?.surfaced_count || 0);
  const hasLatestScan = !!latestScan;

  if ((profilesLoading && !profile.id) || oppsLoading) {
    return <window.DashboardSkeleton />;
  }

  // Pre-scan empty state — mission brief + radar + ghost cards, all driven
  // by the user's saved business profile. Shown only when no scan has ever
  // run for this profile yet.
  if (firstScan && !isScanning) {
    return (
      <window.DashboardFirstScan
        profile={profile}
        onNavigate={onNavigate}
        onScanNow={onScanNow}
      />
    );
  }

  // Live scan view — replaces the dashboard skeleton during a first scan
  // (or any scan against an empty inbox) so the user can watch progress.
  if (isScanning && (firstScan || opps.length === 0)) {
    return <window.DashboardScanning profile={profile} isFirstScan={firstScan} scanSubreddits={scanSubreddits} />;
  }

  // Cluster comment opps by thread BEFORE slicing so a 4-comment thread
  // doesn't eat half the dashboard's top-list. Falls back gracefully if
  // window.clusterOpps hasn't loaded yet (component still mounting).
  const clusteredOpps = window.clusterOpps ? window.clusterOpps(opps) : opps;
  const top = clusteredOpps.slice(0, 10);

  return (
    <div className="page page-v2 screen-enter">
      <Seo title="Dashboard" noindex />
      <div className="page-header page-header-v2">
        <div>
          {lastScanLabel && (
            <div className="page-eyebrow">
              <span className="page-eyebrow-dot" />
              Last scan · {lastScanLabel}
            </div>
          )}
          <h1 className="page-title">{greeting}, {displayName}</h1>
          <p className="page-subtitle">
            {opps.length > 0 ? (
              <>
                <span className="page-subtitle-strong">
                  {opps.length} active opportunit{opps.length === 1 ? "y" : "ies"}
                </span>
                {" "}across {monitored} subreddit{monitored === 1 ? "" : "s"}.
              </>
            ) : (
              <>Run a scan to populate your radar.</>
            )}
          </p>
          {hasLatestScan && (
            <ScanSummaryBanner
              candidates={scanCandidates}
              surfaced={scanSurfaced}
              activeCount={opps.length}
            />
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => onNavigate("profile")}>
            <window.I.Edit size={14} />
            Edit profile
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => onNavigate("opportunities")}>
            View all opportunities
            <window.I.ArrowRight size={14} />
          </button>
        </div>
      </div>

      {isScanning && !firstScan && (
        <div
          style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 14px", marginBottom: 16, borderRadius: 10,
            border: "1px solid var(--accent-border, var(--border-subtle))",
            background: "var(--accent-subtle, var(--bg-subtle))",
            fontSize: 13, color: "var(--text-secondary)",
          }}
        >
          <span
            style={{
              width: 8, height: 8, borderRadius: "50%",
              background: "var(--accent)", flex: "0 0 auto",
              animation: "live-pulse 1400ms ease-in-out infinite",
            }}
          />
          <span>
            {newCount > 0 ? (
              <>
                <strong style={{ color: "var(--text-primary)" }}>{newCount}</strong>
                {" "}new opportunit{newCount === 1 ? "y" : "ies"} so far — still scanning,
                more will appear here as they're found.
              </>
            ) : (
              <>Still scanning — new opportunities will appear here as they're found.</>
            )}
          </span>
          <window.I.Sparkles
            size={13}
            style={{ marginLeft: "auto", color: "var(--accent)", animation: "spin 900ms linear infinite" }}
          />
        </div>
      )}

      <div className="stat-grid stat-grid-v2">
        <StatTile
          label="New opportunities"
          value={newCount}
          meta={<span>since last visit</span>}
          icon="Sparkles"
          accent
        />
        <StatTile
          label="High-confidence matches"
          value={highCount}
          meta={<span>Judged High</span>}
          icon="ShieldCheck"
        />
        <StatTile
          label="Opportunities reviewed"
          value={opps.filter((o) => o.feedback).length}
          meta={<span>helps next scan</span>}
          icon="ThumbsUp"
        />
        <RadarTile profile={profile} />
      </div>

      <div className="dash-grid">
        <div className="card dash-card">
          <div className="card-header card-header-v2">
            <span className="card-title">Top opportunities</span>
            <span className="pill pill-accent" style={{ marginLeft: 6 }}>{top.length}</span>
            <div className="spacer" />
            <button className="btn btn-ghost btn-sm" onClick={() => onNavigate("opportunities")}>
              View all
              <window.I.ChevronRight size={12} />
            </button>
          </div>
          <ol className="opp-brief-list">
            {top.map((o, i) => (
              o.kind === "comment-cluster" ? (
                <window.ThreadClusterBriefRow
                  key={o.id}
                  opp={o}
                  index={i}
                  onOpen={onOpenOpp}
                  scoreMode={scoreMode}
                />
              ) : (
                <window.OpportunityBriefRow
                  key={o.id}
                  opp={o}
                  index={i}
                  onOpen={onOpenOpp}
                  onFeedback={onFeedback}
                  onGenerate={onOpenOpp}
                  scoreMode={scoreMode}
                />
              )
            ))}
          </ol>
        </div>

        <div className="dash-side">
          <SubredditBreakdown opps={opps} />
          <ActivityCard latestScan={latestScan} opps={opps} />
          <TrustCard onNavigate={onNavigate} />
        </div>
      </div>
    </div>
  );
}

/* Color of a subreddit chip — stable per name. */
const SUB_PALETTE = [
  "oklch(0.62 0.13 268)",
  "oklch(0.6 0.13 200)",
  "oklch(0.62 0.12 160)",
  "oklch(0.65 0.13 50)",
  "oklch(0.6 0.14 340)",
  "oklch(0.58 0.13 30)",
];
function subColor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return SUB_PALETTE[Math.abs(h) % SUB_PALETTE.length];
}

function SubredditBreakdown({ opps }) {
  const map = new Map();
  opps.forEach((o) => {
    if (!map.has(o.subreddit)) {
      map.set(o.subreddit, { total: 0, high: 0, med: 0, low: 0, newCount: 0 });
    }
    const r = map.get(o.subreddit);
    r.total++;
    if (o.tier === "high") r.high++;
    else if (o.tier === "medium") r.med++;
    else r.low++;
    if (o.isNew) r.newCount++;
  });

  const rows = Array.from(map.entries())
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, 5);
  const max = Math.max(1, ...rows.map((r) => r[1].total));
  const totalHigh = rows.reduce((a, [, r]) => a + r.high, 0);
  const totalAll = rows.reduce((a, [, r]) => a + r.total, 0);

  return (
    <div className="card sub-bd">
      <div className="card-header card-header-v2">
        <window.I.ChartBar size={14} style={{ color: "var(--text-tertiary)" }} />
        <span className="card-title">By subreddit</span>

      </div>

      <div className="sub-bd-list">
        {rows.map(([sub, r], i) => (
          <SubRow key={sub} sub={sub} r={r} max={max} delay={120 + i * 80} />
        ))}
      </div>

      <div className="sub-bd-legend">
        <span className="sub-bd-legend-item">
          <span className="sub-bd-swatch tier-high" />
          High
        </span>
        <span className="sub-bd-legend-item">
          <span className="sub-bd-swatch tier-med" />
          Medium
        </span>
        <span className="sub-bd-legend-item">
          <span className="sub-bd-swatch tier-low" />
          Low
        </span>
      </div>
    </div>
  );
}

function SubRow({ sub, r, max, delay }) {
  const name = sub.replace(/^r\//, "");
  const initial = name.charAt(0).toUpperCase();
  const color = subColor(sub);
  const widthPct = (r.total / max) * 100;

  return (
    <div className="sub-row" style={{ animationDelay: `${delay}ms` }}>
      <div className="sub-row-head">
        <span className="sub-avatar" style={{ background: color }}>
          {initial}
        </span>
        <span className="sub-name">{sub}</span>
        {r.newCount > 0 && (
          <span className="sub-new-tag">
            <span className="sub-new-dot" />
            {r.newCount} new
          </span>
        )}
        <div className="spacer" />
        <span className="sub-count">{r.total}</span>
      </div>
      <div className="sub-track">
        <div
          className="sub-bar"
          style={{ width: `${widthPct}%`, animationDelay: `${delay}ms` }}
        >
          {r.high > 0 && <span className="sub-bar-seg tier-high" style={{ flex: r.high }} />}
          {r.med > 0 && <span className="sub-bar-seg tier-med" style={{ flex: r.med }} />}
          {r.low > 0 && <span className="sub-bar-seg tier-low" style={{ flex: r.low }} />}
        </div>
      </div>
    </div>
  );
}

function ActivityCard({ latestScan, opps }) {
  // Compose recent activity from real data: the latest scan + the user's
  // recent feedback marks. Falls back to a single "ready for first scan"
  // row when there's no history yet so the card never shows stale fakes.
  const events = React.useMemo(() => {
    const out = [];
    if (latestScan) {
      const iso = latestScan.finished_at || latestScan.started_at;
      out.push({
        t: iso ? window.relativeAgo(iso) : "Recently",
        text: `Scan ${latestScan.status || "complete"} — ${
          latestScan.candidates_count ?? "?"
        } candidates, ${latestScan.surfaced_count ?? 0} surfaced`,
        icon: "Refresh",
      });
    }
    const reviewed = opps.filter((o) => o.feedback).length;
    if (reviewed > 0) {
      out.push({
        t: "This session",
        text: `Marked ${reviewed} opportunit${reviewed === 1 ? "y" : "ies"}`,
        icon: "ThumbsUp",
      });
    }
    if (out.length === 0) {
      out.push({
        t: "Now",
        text: "Ready for your first scan. Use Scan now in the top bar.",
        icon: "Sparkles",
      });
    }
    return out;
  }, [latestScan, opps]);

  return (
    <div className="card">
      <div className="card-header card-header-v2">
        <window.I.Clock size={14} style={{ color: "var(--text-tertiary)" }} />
        <span className="card-title">Recent activity</span>
      </div>
      <div className="activity-list">
        {events.map((e, i) => {
          const Ic = window.I[e.icon];
          return (
            <div key={i} className="activity-row">
              <div className="activity-icon"><Ic size={12} /></div>
              <div className="activity-text">
                <div className="activity-title">{e.text}</div>
                <div className="activity-time">{e.t}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TrustCard({ onNavigate }) {
  return (
    <div className="card trust-card">
      <div className="trust-card-body">
        <div className="trust-card-head">
          <window.I.Bell size={16} style={{ color: "var(--accent)" }} />
          <div className="trust-card-title">Stay ahead of high-intent conversations</div>
        </div>
        <div className="trust-card-desc">
          Enable alerts to get notified about new high-intent opportunities.
        </div>
        <button
          className="btn btn-ghost btn-sm trust-card-link"
          onClick={() => onNavigate("settings", {
            state: { spotlightSection: "notifications" },
          })}
        >
          Turn on alerts
          <window.I.ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
}

window.DashboardScreen = DashboardScreen;
